import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
    LayoutDashboard, 
    Ship, 
    AlertTriangle, 
    Flame, 
    Phone, 
    ClipboardCheck, 
    BookOpen, 
    KeyRound, 
    FileText, 
    Users, 
    Settings, 
    LogOut,
    Menu,
    X,
    ChevronDown,
    ChevronRight,
    Bell,
    Navigation,
    MapPin,
    Truck,
    Gauge,
    FileWarning,
    Fuel,
    Wrench,
    Star,
    Activity,
    Trophy,
    Shield,
    Anchor,
    Cog,
    Calendar,
    Sun,
    Moon,
    Radar,
    MapPinned
} from 'lucide-react';
import { DelimaLogo } from './DelimaLogo';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

const ALL_ROLES = ['admin', 'gestor', 'operador', 'consulta'];

const sidebarSections = [
    {
        id: 'monitoramento',
        label: 'Monitoramento',
        icon: LayoutDashboard,
        items: [
            { path: '/dashboard', label: 'Painel', icon: LayoutDashboard, roles: ALL_ROLES },
            { path: '/operacional/viagens', label: 'Viagens', icon: Navigation, roles: ALL_ROLES },
            { path: '/acompanhamento-viagem', label: 'Acomp. Viagem', icon: MapPin, roles: ALL_ROLES },
            { path: '/operacional/rdo', label: 'RDO', icon: FileText, roles: ALL_ROLES },
            { path: '/operacional/dashboard', label: 'Operacao do Painel', icon: Gauge, roles: ['admin', 'gestor', 'consulta'] },
            { path: '/operacional/boletins', label: 'Boletim Diario', icon: FileText, roles: ALL_ROLES },
            { path: '/roos', label: 'ROO', icon: AlertTriangle, roles: ALL_ROLES },
            { path: '/sinistros', label: 'Sinistros', icon: Flame, roles: ALL_ROLES },
            { path: '/pas', label: 'PA', icon: Phone, roles: ALL_ROLES },
            { path: '/checklists', label: 'Lista de Verificacao', icon: ClipboardCheck, roles: ALL_ROLES },
            { path: '/lptes', label: 'LPTE', icon: BookOpen, roles: ALL_ROLES },
            { path: '/access-logs', label: 'Acesso', icon: KeyRound, roles: ALL_ROLES },
            { path: '/reports', label: 'Relatorios', icon: FileText, roles: ['admin', 'gestor', 'consulta'] },
            { path: '/rastreamento-ais', label: 'Rastreamento AIS', icon: Radar, roles: ALL_ROLES },
            { path: '/geofences', label: 'Geofences & MMSI', icon: MapPinned, roles: ['admin', 'gestor'] },
            { path: '/users', label: 'Usuarios', icon: Users, roles: ['admin'] },
        ],
    },
    {
        id: 'sesmt',
        label: 'SESMT',
        icon: Shield,
        items: [
            { path: '/nao-conformidades', label: 'Nao Conformidade', icon: FileWarning, roles: ALL_ROLES },
        ],
    },
    {
        id: 'manutencao',
        label: 'Manutencao',
        icon: Wrench,
        items: [
            { path: '/plano-manutencao', label: 'Plano de Manutencao', icon: Wrench, roles: ALL_ROLES },
            { path: '/operacional/manutencao', label: 'Manutencao', icon: Settings, roles: ALL_ROLES },
            { path: '/operacional/defeitos', label: 'Defeitos', icon: Cog, roles: ALL_ROLES },
            { path: '/ranking-embarcacoes', label: 'Ranking de Embarcacoes', icon: Trophy, roles: ALL_ROLES },
        ],
    },
    {
        id: 'navegacao',
        label: 'Navegacao',
        icon: Anchor,
        items: [
            { path: '/tripulantes', label: 'Tripulantes', icon: Users, roles: ALL_ROLES },
            { path: '/escala-servicos', label: 'Escala de Servicos', icon: Calendar, roles: ALL_ROLES },
            { path: '/passagem-servico', label: 'Passagem de Serviço', icon: ClipboardCheck, roles: ALL_ROLES },
            { path: '/ranking-passagens', label: 'Ranking de Passagens', icon: Trophy, roles: ALL_ROLES },
            { path: '/documentos-bordo', label: 'Documentos', icon: FileText, roles: ALL_ROLES },
            { path: '/operacional/empurradores', label: 'Empurradores', icon: Truck, roles: ALL_ROLES },
            { path: '/operacional/balsas', label: 'Balsas', icon: Ship, roles: ALL_ROLES },
            { path: '/operacional/destinos', label: 'Destinos', icon: MapPin, roles: ALL_ROLES },
        ],
    },
    {
        id: 'operacao',
        label: 'Operacao',
        icon: Activity,
        items: [
            { path: '/controle-ciclo', label: 'Controle de Ciclo', icon: Fuel, roles: ALL_ROLES },
            { path: '/avaliacao-bunker', label: 'Avaliacao do Bunker', icon: Star, roles: ALL_ROLES },
            { path: '/controle-operacional', label: 'Controle Operacional', icon: Activity, roles: ALL_ROLES },
            { path: '/desempenho-plr', label: 'Desempenho PLR', icon: Trophy, roles: ALL_ROLES },
        ],
    },
];

const roleLabels = {
    admin: 'Administrador',
    gestor: 'Gestor',
    operador: 'Operador',
    consulta: 'Consulta'
};

export const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [openSections, setOpenSections] = useState({ monitoramento: true });
    const { user, logout, hasRole } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const toggleSection = (id) => {
        setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Auto-expand the section containing the current route
    React.useEffect(() => {
        for (const section of sidebarSections) {
            if (section.items.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'))) {
                setOpenSections(prev => ({ ...prev, [section.id]: true }));
                break;
            }
        }
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-slate-950 flex">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/60 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:static inset-y-0 left-0 z-50
                w-64 bg-slate-950/95 border-r border-slate-800
                transform transition-transform duration-200 ease-in-out
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                {/* Logo */}
                <div className="h-14 flex items-center justify-between px-4 border-b border-slate-800">
                    <Link to="/dashboard">
                        <DelimaLogo variant="default" />
                    </Link>
                    <button 
                        className="lg:hidden text-slate-400 hover:text-white"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Navigation */}
                <nav className="px-3 py-3 overflow-y-auto h-[calc(100vh-3.5rem)] space-y-1">
                    {sidebarSections.map((section) => {
                        const filteredItems = section.items.filter(item => hasRole(item.roles));
                        if (filteredItems.length === 0) return null;
                        const SectionIcon = section.icon;
                        const isOpen = openSections[section.id] || false;
                        const hasActive = filteredItems.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));

                        return (
                            <div key={section.id} className="mb-0.5">
                                <button
                                    onClick={() => toggleSection(section.id)}
                                    data-testid={`section-${section.id}`}
                                    className={`w-full flex items-center justify-between px-2.5 py-2 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors ${hasActive ? 'text-cyan-400 bg-cyan-500/5' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <SectionIcon className="w-3.5 h-3.5" />
                                        <span>{section.label}</span>
                                    </div>
                                    <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                                </button>
                                {isOpen && (
                                    <div className="mt-0.5 ml-2 space-y-0.5 border-l border-slate-800/60 pl-2">
                                        {filteredItems.map((item) => {
                                            const Icon = item.icon;
                                            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                                            return (
                                                <Link
                                                    key={item.path}
                                                    to={item.path}
                                                    data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                                                    onClick={() => setSidebarOpen(false)}
                                                    className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-colors ${isActive ? 'bg-cyan-500/10 text-cyan-400 font-medium' : 'text-slate-400 hover:text-white hover:bg-slate-800/40'}`}
                                                >
                                                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                                                    <span className="truncate">{item.label}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <header className="h-14 bg-slate-950/95 border-b border-slate-800 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
                    <div className="flex items-center gap-4">
                        <button 
                            className="lg:hidden text-slate-400 hover:text-white"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                        <h1 className="font-heading text-lg font-semibold text-white tracking-tight hidden sm:block">
                            Sala de Monitoramento
                        </h1>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Theme Toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            className="text-slate-400 hover:text-white hover:bg-slate-800"
                            title={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
                            data-testid="btn-toggle-theme"
                        >
                            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </Button>

                        {/* Notifications */}
                        <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-slate-400 hover:text-white hover:bg-slate-800"
                        >
                            <Bell className="w-4 h-4" />
                        </Button>

                        {/* User Menu */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button 
                                    variant="ghost" 
                                    data-testid="user-menu-trigger"
                                    className="flex items-center gap-2 text-slate-300 hover:text-white hover:bg-slate-800"
                                >
                                    <div className="w-7 h-7 rounded-sm bg-cyan-600 flex items-center justify-center text-xs font-bold text-white">
                                        {user?.nome?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <div className="hidden sm:block text-left">
                                        <p className="text-sm font-medium">{user?.nome}</p>
                                        <p className="text-[10px] text-slate-500 font-mono uppercase">
                                            {roleLabels[user?.role]}
                                        </p>
                                    </div>
                                    <ChevronDown className="w-4 h-4 text-slate-500" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-800">
                                <div className="px-2 py-1.5 sm:hidden">
                                    <p className="text-sm font-medium text-white">{user?.nome}</p>
                                    <p className="text-[10px] text-slate-500 font-mono uppercase">
                                        {roleLabels[user?.role]}
                                    </p>
                                </div>
                                <DropdownMenuSeparator className="bg-slate-800 sm:hidden" />
                                <DropdownMenuItem 
                                    className="text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer"
                                    onClick={() => navigate('/settings')}
                                >
                                    <Settings className="w-4 h-4 mr-2" />
                                    Configurações
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-slate-800" />
                                <DropdownMenuItem 
                                    data-testid="btn-logout"
                                    className="text-red-400 hover:text-red-300 hover:bg-slate-800 cursor-pointer"
                                    onClick={handleLogout}
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Sair
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1 p-4 lg:p-6 overflow-auto">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
